import { ApplicationStatusEnum } from '../../../../../../common/general-types/briefs.dto';
import {
  expiryUrgencyFromPercent,
  isAutoDeclineExpired,
  toEpochMs,
  type ExpiryUrgency,
} from '../application-expiry-bar/application-expiry-bar.helpers';

const MINUTE_MS = 60000;
const HOUR_MS = 3600000;
const DAY_MS = 86400000;

const MS_PER_DAY = 86400000;

export type ApplicationStageId =
  | 'invited'
  | 'applied'
  | 'hired'
  | 'started'
  | 'posting'
  | 'completed';

/** How to fill a stage when it is the current one. Change `currentSource` to add percents later. */
export type StageProgressSource =
  | { type: 'full' }
  | { type: 'time-remaining' }
  | { type: 'fixed'; percent: number };

export interface ApplicationStageDef {
  id: ApplicationStageId;
  label: string;
  statuses: readonly ApplicationStatusEnum[];
  currentSource: StageProgressSource;
}

export const APPLICATION_STAGE_PIPELINE: readonly ApplicationStageDef[] = [
  {
    id: 'invited',
    label: 'Invited',
    statuses: [ApplicationStatusEnum.Invited],
    currentSource: { type: 'full' },
  },
  {
    id: 'applied',
    label: 'Applied',
    statuses: [ApplicationStatusEnum.Applied],
    currentSource: { type: 'time-remaining' },
  },
  {
    id: 'hired',
    label: 'Hired',
    statuses: [ApplicationStatusEnum.Hired],
    currentSource: { type: 'full' },
  },
  {
    id: 'started',
    label: 'Started',
    statuses: [ApplicationStatusEnum.Started],
    currentSource: { type: 'full' },
  },
  {
    id: 'posting',
    label: 'Posting',
    statuses: [ApplicationStatusEnum.ReadyToPost],
    currentSource: { type: 'full' },
  },
  {
    id: 'completed',
    label: 'Completed',
    /**
     * Terminal status depends on brief type: social posts often stop at Posted,
     * UGC at Completed, and paid work at Paid. Any of these fills the last stage.
     */
    statuses: [
      ApplicationStatusEnum.Posted,
      ApplicationStatusEnum.Completed,
      ApplicationStatusEnum.Paid,
      ApplicationStatusEnum.BonusPaid,
    ],
    currentSource: { type: 'full' },
  },
];

const TERMINAL_STAGE_INDEX: Partial<Record<ApplicationStatusEnum, number>> = {
  [ApplicationStatusEnum.InviteDeclined]: 0,
  [ApplicationStatusEnum.InviteRemoved]: 0,
  [ApplicationStatusEnum.ApplicationDeclined]: 1,
  [ApplicationStatusEnum.HiredRemoved]: 2,
};

export type StageFillTone = 'empty' | 'complete' | 'expired' | ExpiryUrgency;

export interface ApplicationStageSegment {
  id: ApplicationStageId;
  label: string;
  percent: number;
  tone: StageFillTone;
  fillClass: string;
}

export function isAppliedAutoDeclineExpired(
  applicant: {
    applicationStatus: ApplicationStatusEnum;
    autoDeclineAt?: string | Date | null;
  },
  nowMs: number,
): boolean {
  if (applicant.applicationStatus !== ApplicationStatusEnum.Applied) {
    return false;
  }
  return isAutoDeclineExpired(applicant.autoDeclineAt, nowMs);
}

export function canOpenReviewWindowExplainer(
  segmentId: string,
  showExpiryCopy: boolean,
): boolean {
  return segmentId === 'applied' && showExpiryCopy;
}

export function expiryTimeRemainingPercent(
  autoDeclineAt: string | Date | null | undefined,
  autoDeclineDays: number | null | undefined,
  nowMs: number,
  appliedAt?: string | Date | null,
): number | null {
  const end = toEpochMs(autoDeclineAt);
  if (end == null) {
    return null;
  }

  const remainingMs = end - nowMs;
  if (remainingMs <= 0) {
    return 0;
  }

  const windowMs =
    typeof autoDeclineDays === 'number' && autoDeclineDays > 0
      ? autoDeclineDays * MS_PER_DAY
      : windowMsFromAppliedAt(appliedAt, end);

  if (windowMs == null || windowMs <= 0) {
    return null;
  }

  return clampPercent((remainingMs / windowMs) * 100);
}

function windowMsFromAppliedAt(
  appliedAt: string | Date | null | undefined,
  endMs: number,
): number | null {
  const start = toEpochMs(appliedAt);
  if (start == null) {
    return null;
  }
  return endMs - start;
}

export function isAutoDeclineEnabled(
  autoDeclineDays: number | null | undefined,
  autoDeclineAt: string | Date | null | undefined,
): boolean {
  if (autoDeclineDays === null || autoDeclineDays === 0) {
    return false;
  }
  if (typeof autoDeclineDays === 'number' && autoDeclineDays > 0) {
    return true;
  }
  return autoDeclineAt != null && autoDeclineAt !== '';
}

/** Social briefs carry a shout brief. UGC briefs do not, and they skip Posting. */
export function briefIncludesPostingStage(
  shoutBrief: { socialPlatform?: number | null } | null | undefined,
): boolean {
  return shoutBrief != null;
}

export function pipelineForBrief(includePosting: boolean): readonly ApplicationStageDef[] {
  if (includePosting) {
    return APPLICATION_STAGE_PIPELINE;
  }
  return APPLICATION_STAGE_PIPELINE.filter((stage) => stage.id !== 'posting');
}

export function stageIndexForStatus(
  status: ApplicationStatusEnum,
  includePosting = true,
): number | null {
  const pipeline = pipelineForBrief(includePosting);
  const pipelineIndex = pipeline.findIndex((stage) => stage.statuses.includes(status));
  if (pipelineIndex >= 0) {
    return pipelineIndex;
  }
  if (!includePosting && status === ApplicationStatusEnum.ReadyToPost) {
    return pipeline.findIndex((stage) => stage.id === 'started');
  }
  const terminalIndex = TERMINAL_STAGE_INDEX[status];
  return terminalIndex ?? null;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value));
}

function currentStagePercent(
  source: StageProgressSource,
  timeRemainingPercent: number | null,
  autoDeclineEnabled: boolean,
): number {
  if (source.type === 'fixed') {
    return clampPercent(source.percent);
  }
  if (source.type === 'time-remaining' && autoDeclineEnabled && timeRemainingPercent != null) {
    return clampPercent(timeRemainingPercent);
  }
  return 100;
}

function fillClassForTone(tone: StageFillTone): string {
  if (tone === 'plenty') {
    return 'bg-green-500';
  }
  if (tone === 'soon') {
    return 'bg-amber-500';
  }
  if (tone === 'urgent') {
    return 'bg-red-400';
  }
  if (tone === 'expired') {
    return 'bg-red-500';
  }
  if (tone === 'complete') {
    return 'bg-violet-500';
  }
  return '';
}

export function buildStageSegments(options: {
  status: ApplicationStatusEnum;
  timeRemainingPercent: number | null;
  autoDeclineEnabled: boolean;
  expired?: boolean;
  includePosting?: boolean;
}): ApplicationStageSegment[] {
  const includePosting = options.includePosting !== false;
  const pipeline = pipelineForBrief(includePosting);
  const currentIndex = stageIndexForStatus(options.status, includePosting);
  const expired =
    options.expired === true ||
    (options.autoDeclineEnabled &&
      options.timeRemainingPercent != null &&
      options.timeRemainingPercent <= 0);

  return pipeline.map((stage, index) => {
    if (currentIndex == null) {
      return {
        id: stage.id,
        label: stage.label,
        percent: 0,
        tone: 'empty' as const,
        fillClass: 'application-progress-bar-inner',
      };
    }

    const isPast = index < currentIndex;
    const isCurrent = index === currentIndex;
    const isExpiredCurrent =
      expired && isCurrent && stage.currentSource.type === 'time-remaining';
    const percent = isPast
      ? 100
      : isExpiredCurrent
        ? 100
        : isCurrent
          ? currentStagePercent(
              stage.currentSource,
              options.timeRemainingPercent,
              options.autoDeclineEnabled,
            )
          : 0;

    const usesTimeTone =
      isCurrent &&
      !isExpiredCurrent &&
      stage.currentSource.type === 'time-remaining' &&
      options.autoDeclineEnabled &&
      options.timeRemainingPercent != null;

    const tone: StageFillTone = isExpiredCurrent
      ? 'expired'
      : percent <= 0
        ? 'empty'
        : usesTimeTone
          ? expiryUrgencyFromPercent(percent)
          : 'complete';

    return {
      id: stage.id,
      label: stage.label,
      percent,
      tone,
      fillClass: ['application-progress-bar-inner', fillClassForTone(tone)].filter(Boolean).join(' '),
    };
  });
}

export interface StageStatusEvent {
  status: number;
  createdDate?: string | Date | null;
}

export interface StageStatusCopy {
  statusTitle: string;
  statusCaption: string;
  statusTitleClass: string;
  statusCaptionClass: string;
}

const STAGE_VERBS: Record<ApplicationStageId, string> = {
  invited: 'invited',
  applied: 'applied',
  hired: 'hired',
  started: 'started',
  posting: 'ready to post',
  completed: 'completed',
};

function currentStatusCopy(status: ApplicationStatusEnum): { title: string; verb: string } | null {
  switch (status) {
    case ApplicationStatusEnum.Invited:
      return { title: 'Invited', verb: 'invited' };
    case ApplicationStatusEnum.Applied:
      return { title: 'Applied', verb: 'applied' };
    case ApplicationStatusEnum.Hired:
      return { title: 'Hired', verb: 'hired' };
    case ApplicationStatusEnum.Started:
      return { title: 'Started', verb: 'started' };
    case ApplicationStatusEnum.ReadyToPost:
      return { title: 'Posting', verb: 'ready to post' };
    case ApplicationStatusEnum.Posted:
      return { title: 'Posted', verb: 'posted' };
    case ApplicationStatusEnum.Completed:
      return { title: 'Completed', verb: 'completed' };
    case ApplicationStatusEnum.Paid:
      return { title: 'Paid', verb: 'paid' };
    case ApplicationStatusEnum.BonusPaid:
      return { title: 'Bonus paid', verb: 'bonus paid' };
    case ApplicationStatusEnum.InviteDeclined:
      return { title: 'Invite declined', verb: 'declined' };
    case ApplicationStatusEnum.ApplicationDeclined:
      return { title: 'Declined', verb: 'declined' };
    case ApplicationStatusEnum.InviteRemoved:
      return { title: 'Invite removed', verb: 'removed' };
    case ApplicationStatusEnum.HiredRemoved:
      return { title: 'Un-hired', verb: 'un-hired' };
    default:
      return null;
  }
}

/** Tail after the stage verb: "3 days ago", "yesterday", "just now". */
export function formatStageElapsed(elapsedMs: number): string {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 2 * MINUTE_MS) {
    return 'just now';
  }
  const minutes = Math.floor(elapsedMs / MINUTE_MS);
  if (minutes < 60) {
    return `${minutes} mins ago`;
  }
  const hours = Math.floor(elapsedMs / HOUR_MS);
  if (hours < 24) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  const days = Math.floor(elapsedMs / DAY_MS);
  if (days === 1) {
    return 'yesterday';
  }
  return `${days} days ago`;
}

function earliestMsForStatuses(
  history: readonly StageStatusEvent[] | null | undefined,
  statuses: readonly number[],
): number | null {
  if (!history?.length) {
    return null;
  }
  let earliest: number | null = null;
  for (const event of history) {
    if (!statuses.includes(event.status)) {
      continue;
    }
    const ms = toEpochMs(event.createdDate);
    if (ms == null) {
      continue;
    }
    if (earliest == null || ms < earliest) {
      earliest = ms;
    }
  }
  return earliest;
}

function enteredAtMs(options: {
  stage: ApplicationStageDef;
  isCurrent: boolean;
  status: ApplicationStatusEnum;
  history: readonly StageStatusEvent[] | null | undefined;
  statusModifiedAt?: string | Date | null;
  createdAt?: string | Date | null;
}): number | null {
  if (options.isCurrent) {
    const exact = earliestMsForStatuses(options.history, [options.status]);
    if (exact != null) {
      return exact;
    }
    const modified = toEpochMs(options.statusModifiedAt);
    if (modified != null) {
      return modified;
    }
  }

  const stageEntered = earliestMsForStatuses(options.history, options.stage.statuses);
  if (stageEntered != null) {
    return stageEntered;
  }

  if (options.stage.id === 'invited' && options.isCurrent) {
    return toEpochMs(options.createdAt);
  }

  return null;
}

export function attachStageStatusCopy(
  segments: readonly ApplicationStageSegment[],
  options: {
    status: ApplicationStatusEnum;
    nowMs: number;
    statusModifiedAt?: string | Date | null;
    createdAt?: string | Date | null;
    history?: readonly StageStatusEvent[] | null;
    density?: 'compact' | 'prominent';
  },
): Array<ApplicationStageSegment & StageStatusCopy> {
  const currentIndex = segments.reduce(
    (found, segment, index) => (segment.percent > 0 ? index : found),
    -1,
  );
  const titleSize = options.density === 'prominent' ? 'text-sm' : 'text-xxs';
  const captionSize = options.density === 'prominent' ? 'text-xs' : 'text-xxs';
  const specific = currentStatusCopy(options.status);

  return segments.map((segment, index) => {
    const stage = APPLICATION_STAGE_PIPELINE.find((item) => item.id === segment.id);
    const isCurrent = index === currentIndex;
    const reached = segment.percent > 0;
    const title = isCurrent && specific ? specific.title : segment.label;
    const verb = isCurrent && specific ? specific.verb : stage ? STAGE_VERBS[stage.id] : segment.label.toLowerCase();
    const entered = stage
      ? enteredAtMs({
          stage,
          isCurrent,
          status: options.status,
          history: options.history,
          statusModifiedAt: options.statusModifiedAt,
          createdAt: options.createdAt,
        })
      : null;
    const caption =
      reached && entered != null
        ? `${verb} ${formatStageElapsed(Math.max(0, options.nowMs - entered))}`
        : '';

    return {
      ...segment,
      statusTitle: title,
      statusCaption: caption,
      statusTitleClass: reached
        ? `m-0 shrink-0 ${titleSize} font-semibold text-primary`
        : `m-0 shrink-0 ${titleSize} font-medium text-muted`,
      statusCaptionClass: `m-0 max-w-full ${captionSize} leading-tight text-secondary xl:shrink-0`,
    };
  });
}
